/// <amd-module name="VSS/Platform/Components/Performance/Performance" />
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { VssComponent } from "../../Layout";
import { IVssPerformanceService, IVssScenario } from "../../Performance";

/**
 * Represents a piece of (potentially-asynchronous) work done
 * during a performance scenario
 */
export interface IPerformanceScenarioPendingWork {
    /**
     * Name of the work -- will be used when logging splits
     */
    name: string;

    /**
     * Mark the work as completed
     */
    complete: () => void;

    /**
     * True if completed has been called
     */
    readonly completed: boolean;
}

/**
 * React context to use for interacting with performance scenarios
 */
export interface IPerformanceScenarioContext {

    /**
     * Registers pending/async work and returns an object
     * that should be completed when the async work is complete
     */
    registerPendingWork: (name: string) => IPerformanceScenarioPendingWork | undefined;

    /**
     * Resets the scenario, creating a new scenario object with the start time of now()
     */
    resetScenario: () => IVssScenario | undefined;
    /**
     * Gets the scenario being tracked.
     */
    getScenario(): IVssScenario | undefined;
}

/**
 * React context to use for interacting with performance scenarios
 */
export const PerformanceScenarioContext = React.createContext<IPerformanceScenarioContext>({
    getScenario: () => undefined,
    registerPendingWork: () => undefined,
    resetScenario: () => undefined
});

/**
 * React hook for accessing the context to interact with performance scenarios
 * @returns The performance scenario context
 */
export function usePerformanceScenarioContext() :  IPerformanceScenarioContext {
    return React.useContext(PerformanceScenarioContext);
}

/**
 * Properties used to render a PerformanceScenario component
 * which triggers a performance scenario when mounted
 * (and any/all pending work is completed)
 */
export interface IPerformanceScenarioProps {
    /**
     * Feature area that owns the scenario
     */
    area: string;

    /**
     * The name of the scenario to trigger
     */
    scenarioName: string;

    /**
     * Explicit start time for the scenario. Otherwise this is considered a page-navigate scenario
     * and is measured from the last page/FPS navigation.
     */
    startTime?: Date;

    /**
     * Telemetry properties to include when firing the scenario event
     */
    telemetryProperties?: { [key: string]: any };

    /**
     * Is this a TTI scenario
     *
     * @default false
     */
    tti?: boolean;
}

/**
 * This component logs a performance scenario when mounted
 * (and any/all pending work is completed)
 */
export class PerformanceScenario extends VssComponent<IPerformanceScenarioProps> {
    private pendingWork: IPerformanceScenarioPendingWork[] = [];
    private scenarioComplete = false;
    private mounted = false;
    private scenario: IVssScenario;

    public render() {
        const { area, scenarioName, startTime, telemetryProperties, tti } = this.props;

        const perfService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        if (startTime) {
            this.scenario = perfService.startScenario(scenarioName, !!tti, startTime.getTime(), telemetryProperties);
        } else {
            this.scenario = perfService.startScenarioFromNavigation(scenarioName, !!tti, telemetryProperties);
        }
        this.scenario.featureArea = area;

        return (
            <PerformanceScenarioContext.Provider
                value={{
                    getScenario: () => this.scenario,
                    registerPendingWork: (name: string) => {
                        const work = {
                            name,
                            completed: false,
                            complete: () => {
                                if (!work.completed) {
                                    work.completed = true;
                                    perfService.addSplitTiming(name + ".end", scenarioName);
                                    this.checkForScenarioComplete();
                                }
                            }
                        };
                        this.pendingWork.push(work);
                        perfService.addSplitTiming(name + ".start", scenarioName);
                        return work;
                    },
                    resetScenario: () => {
                        this.scenarioComplete = false;
                        this.pendingWork.length = 0;
                        this.scenario = perfService.startScenario(scenarioName, !!tti, Date.now(), telemetryProperties);
                        return this.scenario;
                    }
                }}
            >
                {this.props.children}
            </PerformanceScenarioContext.Provider>
        );
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this.mounted = true;
        this.checkForScenarioComplete();
    }

    public componentWillUnmount() {
        super.componentWillUnmount();
        this.mounted = false;
    }

    public componentDidUpdate(prevProps: IPerformanceScenarioProps, prevState: {}): void {
        super.componentDidUpdate(prevProps, prevState);
        this.checkForScenarioComplete();
    }

    private checkForScenarioComplete(): void {
        if (this.mounted && !this.scenarioComplete && this.pendingWork.every(p => p.completed)) {
            this.scenarioComplete = true;
            this.scenario.complete();
        }
    }
}
